import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from 'sonner';
import UbuyHeader from './components/header/UbuyHeader';
import CartSidebar from './components/layout/CartSidebar';
import UbuyFooter from './components/layout/UbuyFooter';
import { ErrorBoundary } from './components/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';

import ScrollToTop from './components/layout/ScrollToTop';
import { env } from './lib/env';

const HomePage = lazy(() => import('./pages/HomePage'));
const ChatWidget = lazy(() => import('./components/chat/ChatWidget'));
const Catalog = lazy(() => import('./pages/Catalog'));
const CollectionsPage = lazy(() => import('./pages/CollectionsPage'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const CartPage = lazy(() => import('./pages/CartPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const AuthResetPassword = lazy(() => import('./pages/AuthResetPassword'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const DeliverySelectionPage = lazy(() => import('./pages/DeliverySelectionPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CargoBranchesPage = lazy(() => import('./pages/CargoBranchesPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const DataDeletionPage = lazy(() => import('./pages/DataDeletionPage'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center px-4">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-primary" />
    </div>
  );
}

function AppProviders() {
  return (
    <ThemeProvider>
      <WishlistProvider>
        <CartProvider>
          <Routes>
            <Route element={<AppShell eagerAuth={false} />}>
              <Route path="/" element={<HomePage />} />
              {env.ENABLE_COLLECTIONS && <Route path="/collections" element={<CollectionsPage />} />}
              <Route path="/products" element={<Catalog />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/cargo-branches" element={<CargoBranchesPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/data-deletion" element={<DataDeletionPage />} />
            </Route>

            <Route element={<AppShell eagerAuth={true} />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/auth/reset" element={<AuthResetPassword />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/orders/:id" element={<OrderDetailPage />} />
                <Route path="/orders/:id/delivery-selection" element={<DeliverySelectionPage />} />
                <Route path="/orders/:id/confirmation" element={<OrderConfirmationPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
            </Route>
          </Routes>
        </CartProvider>
      </WishlistProvider>
    </ThemeProvider>
  );
}

function AppShell({ eagerAuth }: { eagerAuth: boolean }) {
  return (
    <AuthProvider eager={eagerAuth}>
      <Layout />
    </AuthProvider>
  );
}

// Layout компонент: Хуудас бүрийн байршлыг зохицуулна
function Layout() {
  const location = useLocation();

  // Хэрэв бид '/customize' хуудас дээр байгаа бол тусгай горим (Full Screen)
  const isConfigurator = location.pathname === '/customize';

  return (
    <div className={`min-h-screen bg-background text-foreground font-sans transition-colors duration-300 ${isConfigurator ? 'overflow-hidden' : ''}`}>
      <Toaster
        position="top-right"
        richColors
        expand={false}
        toastOptions={{
          style: {
            zIndex: 9999,
            maxWidth: '400px',
            pointerEvents: 'auto',
          },
          duration: 3000,
          className: 'pointer-events-auto',
        }}
        style={{
          right: '1rem',
          top: '5rem',
        }}
      />
      <ScrollToTop />
      {/* Configurator дээр үндсэн Header болон Сагсыг НУУНА */}
      {!isConfigurator && <UbuyHeader />}
      {!isConfigurator && <CartSidebar />}
      {!isConfigurator && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}

      <main>
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {!isConfigurator && <UbuyFooter />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppProviders />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
